<template>
    <div
        class="shadow-xs bg-gray-50 flex flex-row focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
        :class="props.scrollContainer === 'Timeline' ? 'w-full max-h-full overflow-auto' : 'w-max min-w-full'"
    >
        <!-- When the timeline scrolls itself, self-start opts out of the scroll container's stretch so
             this element takes the height of the rows instead of the visible box. The period expanders
             stretch to it and would otherwise stop at the fold once the rows scroll. -->
        <div class="flex flex-row min-h-full" :class="props.scrollContainer === 'Timeline' ? 'self-start' : 'h-full'">
            <div ref="horizontalScrollDetectorRef" />

            <!-- Location pane -->
            <div
                ref="locationPaneRef"
                class="border-r-2 border-r-gray-300 outline-hidden bg-gray-200 sticky left-0 z-20 flex flex-col"
            >
                <div ref="verticalScrollDetectorRef" />

                <div ref="firstColumnHeaderRef" class="flex flex-col sticky top-0 z-40">
                    <!-- Conflict markers -->
                    <!-- Months -->
                    <!-- Calendar weeks -->
                    <div
                        class="h-17.75 w-full bg-gray-50 timeline-border-right timeline-border-right-2 timeline-border-right-gray-300"
                    />

                    <!-- Site row -->
                    <HeaderFirstColumnCell
                        :conflicts-count="props.siteConflictsCount"
                        :label="props.headerName"
                        :error-message="siteHasLocations ? undefined : $t('error-no-locations-in-site')"
                        :link-target="props.headerLinkTarget"
                        :checkbox-status="headerCheckboxStatus"
                        :checkbox-tooltip="headerCheckboxTooltip"
                        @toggle-checkbox="toggleHeaderCheckbox"
                        @mouseover="hoverTimelinePart(null, null, props.siteConflictsCount.total > 0)"
                    />
                </div>

                <div v-if="$slots['first-column-rows']" class="flex flex-col gap-y-px">
                    <slot name="first-column-rows" />
                </div>
            </div>

            <!-- Previous period pane -->
            <div v-if="previousPeriodLabel" class="relative z-10 flex items-stretch">
                <PreviousNextPeriodColumn
                    icon-direction="left"
                    :label="previousPeriodLabel"
                    :is-disabled="isPreviousPeriodDisabled"
                    @click="$emit('expand-to-previous-month')"
                    @mouseover="hoverTimelinePart(null, null, false)"
                />
            </div>

            <!-- Days pane -->
            <div class="relative z-10 flex flex-col h-full grow">
                <TimelineHeader
                    ref="timelineHeaderRef"
                    class="flex flex-col sticky top-0 z-40"
                    :days-count="headerDays.length"
                >
                    <!-- Conflict markers -->
                    <HeaderConflictDay
                        v-for="day in headerDays"
                        :key="day.columnId"
                        :conflict-status="day.conflictStatus"
                        :formatted-date="day.formattedDate"
                        @mouseover="hoverTimelinePart(null, null, false)"
                    />

                    <!-- Months -->
                    <HeaderMonth
                        v-for="month in headerMonths"
                        :key="month.startColumnIndex"
                        :days-count="month.daysCount"
                        :label="month.label"
                        :start-column-index="month.startColumnIndex"
                        @mouseover="hoverTimelinePart(null, null, false)"
                    />

                    <!-- Calendar weeks -->
                    <HeaderCalendarWeek
                        v-for="(calendarWeek, calendarWeekIndex) in headerCalendarWeeks"
                        :key="calendarWeek.startColumnIndex"
                        :days-count="calendarWeek.daysCount"
                        :is-last-calendar-week="calendarWeekIndex === headerCalendarWeeks.length - 1"
                        :label="calendarWeek.label"
                        :start-column-index="calendarWeek.startColumnIndex"
                        @mouseover="hoverTimelinePart(null, null, false)"
                    />

                    <!-- Header days -->
                    <HeaderDay
                        v-for="day in headerDays"
                        :key="day.columnId"
                        :is-work-day="day.isWorkDay"
                        :is-column-hovered="hoveredColumnId === day.columnId"
                        :is-conflict-cell-hovered="isConflictCellHovered"
                        :day-number="day.number"
                        :day-full-name="day.fullName"
                        :day-abbreviation="day.abbreviation"
                        :is-first-column="day.isFirstColumn"
                        @mouseover="hoverTimelinePart(null, day.columnId, day.conflictStatus !== 'No-Conflict')"
                    />
                </TimelineHeader>

                <div
                    v-if="$slots['days-rows']"
                    class="flex flex-col gap-px bg-gray-300 border-b border-b-gray-200 border-r border-r-gray-300 w-max -mt-px h-max"
                >
                    <slot name="days-rows" />
                </div>
            </div>

            <!-- Next period pane -->
            <div v-if="nextPeriodLabel" class="relative z-10 flex items-stretch">
                <PreviousNextPeriodColumn
                    icon-direction="right"
                    :label="nextPeriodLabel"
                    :is-disabled="isNextPeriodDisabled"
                    @click="$emit('expand-to-next-month')"
                    @mouseover="hoverTimelinePart(null, null, false)"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFluent } from 'fluent-vue';
import { DateTime } from 'luxon';
import { computed, isVNode, onUnmounted, provide, ref, type Slot, toRef, useSlots, type VNodeArrayChildren } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { UpdateAllocationType } from './allocation/types';
import HeaderCalendarWeek from './components/HeaderCalendarWeek.vue';
import HeaderConflictDay from './components/HeaderConflictDay.vue';
import HeaderDay from './components/HeaderDay.vue';
import HeaderFirstColumnCell from './components/HeaderFirstColumnCell.vue';
import HeaderMonth from './components/HeaderMonth.vue';
import PreviousNextPeriodColumn from './components/PreviousNextPeriodColumn.vue';
import TimelineHeader from './components/TimelineHeader.vue';
import { useTimelineScrollShadows } from './composables/useTimelineScrollShadows';
import {
    emitDeleteAllocationKey,
    emitUpdateAllocationKey,
    hoveredTimelinePartsKey,
    hoverTimelinePartKey,
    registerAllocationKey,
    timelineRangeKey,
    unregisterAllocationKey,
} from './injectionKeys';
import { type SelectionStatus, useLocationSelectionStore } from './LocationSelection.store';
import { calculateHeaderCalendarWeeks, calculateHeaderDays, calculateHeaderMonths } from './logic/header';
import { useTimelineStore } from './Timeline.store';
import { ConflictsByDay, LocationTypeCategory } from './Timeline.types';

const { $t } = useFluent();

const store = useTimelineStore();
const locationSelectionStore = useLocationSelectionStore();

export type MonthButtonStatus = 'Visible' | 'Disabled' | 'Hidden';

type TimelineProps = {
    headerName: string;
    headerLinkTarget: RouteLocationRaw;
    siteConflictsCount: {
        resolved: number;
        unresolved: number;
        total: number;
    };
    startDateTime: DateTime;
    /**
     * Uses half-open interval (see README.md for details)
     */
    endDateTime: DateTime;
    previousMonthButton: MonthButtonStatus;
    nextMonthButton: MonthButtonStatus;
    conflictsByDay: ConflictsByDay;
    /**
     * 'Page' lets the timeline grow to the size of its rows and leaves scrolling to the page around it.
     * 'Timeline' caps it at the size of its container and scrolls inside, so a container that already
     * fills the viewport gets a single scrollbar.
     */
    scrollContainer: 'Page' | 'Timeline';

    updateAllocation: UpdateAllocationType;
};

const props = defineProps<TimelineProps>();

type TimelineEmits = {
    (
        e: 'delete-allocation',
        allocationId: string,
        locationTypeCategory: LocationTypeCategory,
        onDone: (isSuccess: boolean) => void,
    ): void;
    (e: 'expand-to-previous-month'): void;
    (e: 'expand-to-next-month'): void;
};
const emit = defineEmits<TimelineEmits>();

const durationInDays = computed<number>(() => props.endDateTime.diff(props.startDateTime, 'days').days);
// Separate computeds so a conflict change only invalidates the days, not the calendar weeks and months
const headerDays = computed(() => calculateHeaderDays(props.conflictsByDay, props.startDateTime, durationInDays.value));
const headerCalendarWeeks = computed(() => calculateHeaderCalendarWeeks(props.startDateTime, durationInDays.value));
const headerMonths = computed(() => calculateHeaderMonths(props.startDateTime, durationInDays.value));

const previousPeriodLabel = computed(() => {
    switch (props.previousMonthButton) {
        case 'Visible':
        case 'Disabled': {
            const previousMonth = props.startDateTime.minus({ months: 1 });

            return $t('month-with-year', { month: previousMonth.month, yearString: String(previousMonth.year) });
        }

        case 'Hidden':
            return null;

        default:
            throw new Error(`Unhandled previous month button status: ${props.previousMonthButton satisfies never}`);
    }
});

const nextPeriodLabel = computed(() => {
    switch (props.nextMonthButton) {
        case 'Visible':
        case 'Disabled': {
            const nextMonth = props.endDateTime;
            return $t('month-with-year', { month: nextMonth.month, yearString: String(nextMonth.year) });
        }

        case 'Hidden':
            return null;

        default:
            throw new Error(`Unhandled next month button status: ${props.nextMonthButton satisfies never}`);
    }
});

const isPreviousPeriodDisabled = computed(() => props.previousMonthButton === 'Disabled');
const isNextPeriodDisabled = computed(() => props.nextMonthButton === 'Disabled');

const slots = useSlots();

// Comments and text have symbol vnode types and are never a row. Fragments (produced by `v-if` and
// `v-for`) are symbols too but *do* hold rows, so recurse into them: slot content that is entirely
// `v-if`-wrapped has no real vnode at its top level and would otherwise look empty.
function hasRenderableVNode(nodes: VNodeArrayChildren): boolean {
    return nodes.some((node) => {
        if (Array.isArray(node)) {
            return hasRenderableVNode(node);
        }
        if (!isVNode(node)) {
            return false;
        }
        if (typeof node.type === 'symbol') {
            return Array.isArray(node.children) ? hasRenderableVNode(node.children) : false;
        }
        return true;
    });
}

// A declared slot is always truthy, so `Boolean(slot)` can't tell an empty slot from a populated one.
// Invoke the slot and look for at least one real vnode instead.
function slotHasContent(slot: Slot | undefined): boolean {
    return slot ? hasRenderableVNode(slot()) : false;
}

const siteHasLocations = computed(() => {
    return slotHasContent(slots['first-column-rows']) || slotHasContent(slots['days-rows']);
});

type HeaderCheckboxStatus = InstanceType<typeof HeaderFirstColumnCell>['$props']['checkboxStatus'];

// Maps the store's selection semantics onto the header cell's concrete checkbox states
const SELECTION_TO_CHECKBOX_STATUS = {
    'No-Selection': 'No-Checkbox',
    Selected: 'Checked',
    'Partially-Selected': 'Partially-Checked',
    'Not-Selected': 'Not-Checked',
    'Disabled-Selected': 'Disabled-Checked',
    'Disabled-Not-Selected': 'Disabled-Not-Checked',
} as const satisfies Record<SelectionStatus, HeaderCheckboxStatus>;

const headerCheckboxStatus = computed(
    () => SELECTION_TO_CHECKBOX_STATUS[locationSelectionStore.getTimelineCheckbox.status],
);

const headerCheckboxTooltip = computed(() => {
    const checkbox = locationSelectionStore.getTimelineCheckbox;
    // `tooltip` only exists on the non-`No-Selection` variants of the discriminated union.
    return checkbox.status === 'No-Selection' ? '' : checkbox.tooltip;
});

function toggleHeaderCheckbox() {
    if (locationSelectionStore.isActive) {
        locationSelectionStore.toggleTimelineSelection();
    }
}

const hoveredRowId = ref<string | null>(null);
const hoveredColumnId = ref<string | null>(null);
const isConflictCellHovered = ref(false);

function hoverTimelinePart(rowId: string | null, columnId: string | null, isConflictCell: boolean) {
    hoveredRowId.value = rowId;
    hoveredColumnId.value = columnId;
    isConflictCellHovered.value = isConflictCell;
}

provide(hoverTimelinePartKey, hoverTimelinePart);
provide(hoveredTimelinePartsKey, {
    rowId: hoveredRowId,
    columnId: hoveredColumnId,
    isConflictCell: isConflictCellHovered,
});

// Avoid prop drilling for emits from deep-nested components
provide(emitDeleteAllocationKey, (allocationId, locationTypeCategory, onDone) =>
    emit('delete-allocation', allocationId, locationTypeCategory, (isSuccess) => {
        // Deleting the source allocation ends selection mode. Owned here (Timeline outlives the deleted
        // <Allocation>) so it can't be dropped by that component's state machine being stopped on unmount.
        if (isSuccess) {
            locationSelectionStore.deactivateSelection();
        }
        onDone(isSuccess);
    }),
);
provide(emitUpdateAllocationKey, (...args) => props.updateAllocation(...args));

// Provide allocation registry from the store
provide(registerAllocationKey, (allocationId: string, triggerHighlight: () => void) =>
    store.registerAllocation(allocationId, triggerHighlight),
);
provide(unregisterAllocationKey, (allocationId: string) => store.unregisterAllocation(allocationId));

provide(timelineRangeKey, {
    startDateTime: toRef(props, 'startDateTime'),
    endDateTime: toRef(props, 'endDateTime'),
});

const locationPaneRef = ref<HTMLElement | null>(null);
const verticalScrollDetectorRef = ref<HTMLElement | null>(null);
const horizontalScrollDetectorRef = ref<HTMLElement | null>(null);
const firstColumnHeaderRef = ref<HTMLElement | null>(null);
const timelineHeaderRef = ref<{ ref: HTMLElement | null } | null>(null);

// Names must match the `v-bind()` references in the <style> block below.
const { headerHeight, locationPaneWidth } = useTimelineScrollShadows({
    locationPaneRef,
    verticalScrollDetectorRef,
    horizontalScrollDetectorRef,
    firstColumnHeaderRef,
    timelineHeaderRef,
});

onUnmounted(() => {
    // End selection mode when the whole timeline is destroyed (e.g. navigating away). Collapsing
    // sections keeps the timeline mounted, so it does not trigger this.
    locationSelectionStore.deactivateSelection();
});
</script>

<style>
.scroll-shadow-horizontal::after {
    content: '';
    display: block;
    height: 100%;
    position: absolute;
    left: v-bind('locationPaneWidth');
    top: 0;
    width: v-bind('locationPaneWidth');
    box-shadow: 10px 0 4px -2px color-mix(in oklab, var(--color-gray-900) 20%, transparent) inset; /* = theme('colors.gray.900/0.2') */
    z-index: 20;
    pointer-events: none;
}

.scroll-shadow-vertical::before {
    content: '';
    display: block;
    height: v-bind('headerHeight');
    position: absolute;
    left: 0;
    top: v-bind('headerHeight');
    width: 100%;
    box-shadow: 0 10px 4px -2px color-mix(in oklab, var(--color-gray-900) 20%, transparent) inset; /* = theme('colors.gray.900/0.2') */
    z-index: 20;
    pointer-events: none;
}

.timeline-border-right {
    position: relative;
}

.timeline-border-right-2::after {
    content: '';
    position: absolute;
    top: 0;
    right: -2px;
    width: 2px;
    height: 100%;
}

.timeline-border-right-gray-300::after {
    background-color: var(--color-gray-300);
}
</style>
